/**
 * Fixture audit ledger — the five actor shapes of پیوست ب, in the browser's copy.
 *
 * Fixture mode never contacts a server, so `createFixtureApi().listAudit` has to answer with real
 * ledger rows. What makes this a *copy* rather than a second source of truth is
 * `audit-measured-shapes.json`: that file is written by
 * `tests/test_acs_u16d_audit_shapes.py` out of `GET /ops/v1/audit` on `MemoryStore`, and
 * `web/src/test/auditShapes.test.ts` fails when a row below stops having the key set the door
 * measured, or when the note stops being `AUDIT_LEDGER_NOTE` verbatim. So a server shape changing and
 * a fixture drifting are both red tests, not a page that quietly renders a fiction.
 *
 * Two rules this file obeys:
 *  - **Entries are verbatim objects.** The door declares no summary fields for kinds it does not own,
 *    so nothing here adds a console-only key: a row that carried `shape: 'legacy-flat'` would be the
 *    fixture inventing ledger vocabulary the server never writes. The shape id is the *map key*, not
 *    a field on the row.
 *  - **Nothing is invented as an actor.** The two degraded rows at the bottom are rows whose shapes
 *    the E1 measurement did not produce; they are labelled as unmeasured and are only asserted to
 *    degrade correctly (`absent` / `unrecognised`), never to look like a caller.
 */

import type { AuditEntry } from '../api/types';
import { FIXTURE_DEVICES } from './devices';
import { isoAgo } from './devices';

/**
 * The note the fixture returns, copied from `acs_service/routers/audit.py::AUDIT_LEDGER_NOTE`.
 *
 * Byte-identical on purpose, and checked against the artefact by `test/auditShapes.test.ts`: this is
 * the sentence that separates «no rows matched this filter» from «nothing happened», and a paraphrase
 * in the demo is how an operator learns to distrust the real one.
 */
export const FIXTURE_AUDIT_NOTE =
  'Append-only ledger written by the operations themselves: an entry exists only for an operation ' +
  'that reached the point of recording its intent, so the absence of an entry is not proof that ' +
  'nothing happened — a request rejected before its audit write, or one that failed between the ' +
  'side effect and that write, leaves no trace here. Read-only surface: this API never edits or ' +
  'deletes an entry.';

/** The device the demo ledger is mostly about — the same id `DeviceDetailPage` routes on. */
export const FIXTURE_AUDIT_DEVICE = FIXTURE_DEVICES[0]!.acs_device_id;

/**
 * The door's own page bounds, mirrored from `acs_service/routers/audit.py` (`PAGE_LIMIT`, the shared
 * `ACS_PAGE_LIMIT` ceiling of 200; `DEFAULT_LIMIT` 50, the same default `/ops/v1/jobs` uses). Out of
 * range is a `reference-invalid` there, and the fixture answers the same way so the page's error path
 * is the real one.
 */
export const AUDIT_PAGE_LIMIT = 200;
export const AUDIT_DEFAULT_LIMIT = 50;

/** The ids the artefact measures; a row lives under its shape id, never inside the row. */
export type FixtureAuditShapeId =
  | 'canonical-human'
  | 'canonical-human-device'
  | 'canonical-machine'
  | 'canonical-with-target'
  | 'canonical-with-target-patch'
  | 'canonical-with-target-delete'
  | 'legacy-flat'
  | 'legacy-flat-patch'
  | 'nested-actor'
  | 'session-login'
  | 'session-denied'
  | 'session-logout';

/**
 * The measured shapes, one or more rows each, key sets exactly as the door returned them.
 *
 * Several rows share a shape on purpose: the page must show that the *same* rendering rule covers a
 * second, third and fourth row of a family (different id, different target, same reading), and the
 * guard compares every one of them against the measured key set.
 */
export const FIXTURE_AUDIT_SHAPES: Record<FixtureAuditShapeId, readonly AuditEntry[]> = {
  /* (أ) canonical — a human session spreading `**principal.actor()`. */
  'canonical-human': [
    {
      id: 'fx-audit-h1',
      created_at: isoAgo(4),
      kind: 'permission.change',
      action: 'put',
      cells: [{ capability: 'templates.read', role: 'operator', level: 'read' }],
      actor_kind: 'human',
      actor: 'root',
      user_id: 'user:0f0f0f0f',
      client_id: null,
    },
    {
      id: 'fx-audit-h2',
      created_at: isoAgo(190),
      kind: 'permission.change',
      action: 'put',
      cells: [
        { capability: 'firmware.write', role: 'admin', level: 'edit' },
        { capability: 'campaign.write', role: 'operator', level: 'none' },
      ],
      actor_kind: 'human',
      actor: 'nocsup',
      user_id: 'user:1a1a1a1a',
      client_id: null,
    },
  ],

  /* (أ) canonical, but the row names a device — the shape the per-device tab reads. */
  'canonical-human-device': [
    {
      id: 'fx-audit-d1',
      created_at: isoAgo(11),
      kind: 'device.tag.put',
      device_id: FIXTURE_AUDIT_DEVICE,
      tag: 'customer-call',
      tags: ['customer-call'],
      actor_kind: 'human',
      actor: 'field-technician',
      user_id: 'user:2b2b2b2b',
      client_id: null,
    },
    {
      id: 'fx-audit-d2',
      created_at: isoAgo(64),
      kind: 'device.tag.put',
      device_id: FIXTURE_AUDIT_DEVICE,
      tag: 'warranty-check',
      tags: ['customer-call', 'warranty-check'],
      actor_kind: 'human',
      actor: 'nocsup',
      user_id: 'user:1a1a1a1a',
      client_id: null,
    },
  ],

  /* (أ) canonical, machine caller: `client` appears, and there is no human to name (G-IDN-05). */
  'canonical-machine': [
    {
      id: 'fx-audit-m1',
      created_at: isoAgo(37),
      kind: 'permission.change',
      action: 'put',
      cells: [{ capability: 'optical.read', role: 'user', level: 'read' }],
      actor_kind: 'machine',
      actor: 'test-bss-client',
      user_id: null,
      client_id: 'test-bss-client',
      client: 'test-bss-client',
    },
  ],

  /* (أ′) canonical with a separated target — `create`. `user_id` is the managed user, not the caller. */
  'canonical-with-target': [
    {
      id: 'fx-audit-t1',
      created_at: isoAgo(22),
      kind: 'user.manage',
      action: 'create',
      user_id: 'user:3c3c3c3c',
      username: 'operator-two',
      role: 'operator',
      status: 'active',
      actor_kind: 'human',
      actor: 'root',
      actor_user_id: 'user:0f0f0f0f',
      client_id: null,
    },
    {
      id: 'fx-audit-t2',
      created_at: isoAgo(160),
      kind: 'user.manage',
      action: 'create',
      user_id: 'user:6f6f6f6f',
      username: 'field-crew',
      role: 'operator',
      status: 'active',
      actor_kind: 'human',
      actor: 'root',
      actor_user_id: 'user:0f0f0f0f',
      client_id: null,
    },
  ],

  /* (أ′) `patch`: the before/after pair the create door does not carry, same actor rule. */
  'canonical-with-target-patch': [
    {
      id: 'fx-audit-t3',
      created_at: isoAgo(21),
      kind: 'user.manage',
      action: 'patch',
      user_id: 'user:3c3c3c3c',
      username: 'operator-two',
      before: { role: 'operator', status: 'active' },
      after: { role: 'admin', status: 'active' },
      password_changed: false,
      sessions_revoked: 0,
      actor_kind: 'human',
      actor: 'root',
      actor_user_id: 'user:0f0f0f0f',
      client_id: null,
    },
  ],

  /* (أ′) `delete`: the target is gone, and `user_id` still answers «which user was removed». */
  'canonical-with-target-delete': [
    {
      id: 'fx-audit-t4',
      created_at: isoAgo(140),
      kind: 'user.manage',
      action: 'delete',
      user_id: 'user:7a7a7a7a',
      username: 'temp-summer',
      role: 'operator',
      sessions_revoked: 1,
      actor_kind: 'human',
      actor: 'root',
      actor_user_id: 'user:0f0f0f0f',
      client_id: null,
    },
  ],

  /* (ب) the historical flat `create`: `actor` + `actor_kind`, and no canonical ids. Still a real row. */
  'legacy-flat': [
    {
      id: 'fx-audit-l1',
      created_at: isoAgo(4300),
      kind: 'user.manage',
      action: 'create',
      user_id: 'user:4d4d4d4d',
      username: 'old-operator',
      role: 'operator',
      status: 'active',
      actor: 'root',
      actor_kind: 'human',
    },
    {
      id: 'fx-audit-l2',
      created_at: isoAgo(4310),
      kind: 'user.manage',
      action: 'create',
      user_id: 'user:5e5e5e5e',
      username: 'night-shift',
      role: 'operator',
      status: 'active',
      actor: 'nocsup',
      actor_kind: 'human',
    },
  ],

  /* (ب) the historical flat `patch`: same absence of canonical keys, the patch door's own business keys. */
  'legacy-flat-patch': [
    {
      id: 'fx-audit-l3',
      created_at: isoAgo(4320),
      kind: 'user.manage',
      action: 'patch',
      user_id: 'user:4d4d4d4d',
      username: 'old-operator',
      before: { role: 'operator', status: 'active' },
      after: { role: 'admin', status: 'active' },
      password_changed: false,
      sessions_revoked: 0,
      actor: 'root',
      actor_kind: 'human',
    },
  ],

  /* (ج) the nested actor `routers/profile.py` stores, and `/versions` publishes. */
  'nested-actor': [
    {
      id: 'fx-audit-n1',
      created_at: isoAgo(58),
      kind: 'profile.activate',
      actor: { kind: 'human', subject: 'root' },
      from_version: 3,
      to_version: 4,
      changed_keys: ['defaults.tr069_password', 'fleet.models'],
      rollback_of: null,
    },
  ],

  /* (د) the three session kinds: the self-reported identity of the request, no actor key at all. */
  'session-login': [
    {
      id: 'fx-audit-s1',
      created_at: isoAgo(2),
      kind: 'session.login',
      sid: 'sid:9f00',
      user_id: 'user:0f0f0f0f',
      username: 'root',
      role: 'owner',
      peer: '10.0.4.21',
    },
    {
      id: 'fx-audit-s2',
      created_at: isoAgo(240),
      kind: 'session.login',
      sid: 'sid:9f10',
      user_id: 'user:1a1a1a1a',
      username: 'nocsup',
      role: 'admin',
      peer: '10.0.4.35',
    },
  ],
  'session-denied': [
    {
      id: 'fx-audit-s3',
      created_at: isoAgo(3),
      kind: 'session.denied',
      username: 'root',
      peer: '203.0.113.9',
    },
  ],
  'session-logout': [
    {
      id: 'fx-audit-s4',
      created_at: isoAgo(30),
      kind: 'session.logout',
      sid: 'sid:9f00',
      user_id: 'user:0f0f0f0f',
      username: 'root',
    },
  ],
};

/**
 * Rows whose shapes this file's E1 measurement did not produce, kept because a real ledger holds them.
 *
 * `campaign.auto_*` is the campaign worker's own wording — one `client` string, no `actor`/`actor_kind`
 * at all (`acs_service/campaigns.py:513-517`) — and `nbi.sync` is the sync worker's single line per
 * pass (`docs/API_COVERAGE_FA.md` §۵, the H3 row). Neither is a shape پیوست ب promised, so neither is
 * drift-checked against the artefact; `test/auditShapes.test.ts` only demands that the renderer
 * degrades them (`unrecognised` / `absent`) instead of inventing a caller or blanking the cell.
 */
export const FIXTURE_AUDIT_UNMEASURED: readonly AuditEntry[] = [
  {
    id: 'fx-audit-u1',
    created_at: isoAgo(70),
    kind: 'campaign.auto_halt',
    campaign_id: 'camp:2',
    wave: 1,
    dispatched: 40,
    queued: 0,
    reason: 'failure ratio 18% over the halt threshold',
    client: 'acs-campaigns',
  },
  {
    id: 'fx-audit-u2',
    created_at: isoAgo(1),
    kind: 'nbi.sync',
    informs_purged: 0,
    informs_cutoff: isoAgo(1440),
    retention_days: 30,
    skipped: true,
  },
];

/** Every fixture row, newest first — the order `store.list_audit` answers in. */
export function fixtureAuditRows(): AuditEntry[] {
  return [...Object.values(FIXTURE_AUDIT_SHAPES).flat(), ...FIXTURE_AUDIT_UNMEASURED].sort((a, b) =>
    String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')),
  );
}

/** The artefact's shape ids, so a test can name what must be present without a fixed count. */
export function fixtureAuditShapeIds(): FixtureAuditShapeId[] {
  return Object.keys(FIXTURE_AUDIT_SHAPES) as FixtureAuditShapeId[];
}
